import { useGetList, type Identifier } from "ra-core";
import { useMemo } from "react";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { fullName, type GulaClient } from "../gulaTypes";
import { useCostSettings, useStripeData } from "../money/useMoneyData";
import type {
  AccExpense,
  AccIncome,
  AccSettings,
  AccSubmission,
  AccSupplier,
} from "./accounting";
import { manualIncomeRows, stripeIncomeRows, type IncomeRow } from "./summary";

export const ACCOUNTING_BUCKET = "accounting";

/** Everything the accounting pages need: suppliers, expenses and all income. */
export const useAccountingData = () => {
  const suppliers = useGetList<AccSupplier>("gula_acc_suppliers", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "name", order: "ASC" },
  });
  const expenses = useGetList<AccExpense>("gula_acc_expenses", {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "invoice_date", order: "DESC" },
  });
  const incomes = useGetList<AccIncome>("gula_acc_incomes", {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "income_date", order: "DESC" },
  });
  const clients = useGetList<GulaClient>("gula_clients_summary", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "id", order: "ASC" },
  });
  const accSettings = useGetList<AccSettings>("gula_acc_settings", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });
  const submissions = useGetList<AccSubmission>("gula_acc_submissions", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "sent_at", order: "DESC" },
  });
  const { payments, isPending: stripePending } = useStripeData();
  const { data: settings } = useCostSettings();
  const vatRate = Number(settings?.vat_rate ?? 21);

  const incomeRows = useMemo<IncomeRow[]>(() => {
    const names = new Map(
      (clients.data ?? []).map((client) => [
        String(client.contact_id),
        fullName(client),
      ]),
    );
    const clientIds = new Map(
      (clients.data ?? []).map((client) => [
        String(client.contact_id),
        client.id,
      ]),
    );
    const nameOf = (contactId: Identifier) =>
      names.get(String(contactId)) ?? "Cliente de Stripe";
    return [
      ...stripeIncomeRows(payments, vatRate, nameOf),
      ...manualIncomeRows(incomes.data ?? []),
    ]
      .map((row) => ({
        ...row,
        clientId: row.contactId ? clientIds.get(String(row.contactId)) : null,
      }))
      .sort((a, b) => b.day.localeCompare(a.day));
  }, [payments, incomes.data, clients.data, vatRate]);

  return {
    suppliers: suppliers.data ?? [],
    settings: accSettings.data?.[0],
    submissions: submissions.data ?? [],
    expenses: expenses.data ?? [],
    incomeRows,
    isPending:
      suppliers.isPending ||
      expenses.isPending ||
      incomes.isPending ||
      stripePending,
    error: suppliers.error ?? expenses.error ?? incomes.error,
  };
};

/** Uploads an invoice or receipt to the private bucket. */
export const uploadAccountingFile = async (
  file: File,
  folder: "expenses" | "incomes",
  day: string,
): Promise<{ path: string; name: string }> => {
  const safeName = file.name.replace(/[^\w.-]+/g, "_");
  const path = `${folder}/${day.slice(0, 4) || "sin-fecha"}/${crypto.randomUUID()}-${safeName}`;
  const { error } = await getSupabaseClient()
    .storage.from(ACCOUNTING_BUCKET)
    .upload(path, file, { contentType: file.type || undefined });
  if (error) throw new Error(`No se ha podido subir ${file.name}`);
  return { path, name: file.name };
};

/** Opens a stored file in a new tab (signed link valid for 5 minutes). */
export const openAccountingFile = async (path: string) => {
  // Open the tab right away: browsers block window.open after an await
  const tab = window.open("", "_blank");
  const { data, error } = await getSupabaseClient()
    .storage.from(ACCOUNTING_BUCKET)
    .createSignedUrl(path, 300);
  if (error || !data?.signedUrl) {
    tab?.close();
    throw new Error("No se ha podido abrir el archivo");
  }
  if (tab) tab.location.href = data.signedUrl;
  else window.location.href = data.signedUrl;
};

export const downloadAccountingFile = async (
  path: string,
): Promise<Uint8Array> => {
  const { data, error } = await getSupabaseClient()
    .storage.from(ACCOUNTING_BUCKET)
    .download(path);
  if (error || !data) throw new Error(`No se ha podido descargar ${path}`);
  return new Uint8Array(await data.arrayBuffer());
};

export const removeAccountingFile = async (path: string) => {
  await getSupabaseClient().storage.from(ACCOUNTING_BUCKET).remove([path]);
};

/** Uploads a file generated in the browser (the accountant's package). */
export const uploadAccountingBytes = async (
  bytes: Uint8Array,
  path: string,
  contentType: string,
) => {
  const { error } = await getSupabaseClient()
    .storage.from(ACCOUNTING_BUCKET)
    .upload(path, new Blob([bytes as BlobPart], { type: contentType }), {
      contentType,
      upsert: true,
    });
  if (error) throw new Error("No se ha podido guardar el paquete");
  return path;
};

/** Hands a file generated in the browser to the user. */
export const saveFile = (data: Uint8Array, name: string, type: string) => {
  const url = URL.createObjectURL(new Blob([data as BlobPart], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
